/**
 * BusinessDoc → ProposalDocModel derivation — the ONE place a persisted
 * BusinessDoc becomes the canonical block model that gets hashed and signed.
 *
 * Extracted from ContractsView so the public sign page (SignView) and the
 * internal e-sign panel derive the SAME canonical bytes: a signature request
 * issued over `computeDocHash(buildModelForDoc(doc))` in ContractsView
 * verifies byte-for-byte when SignView recomputes it — "what you saw is
 * what you signed" holds across surfaces because there is exactly one
 * derivation. Pure: no store, no fetch, no ambient state.
 */

import { C } from '../tokens';
import {
  createProposalDoc,
  mergeContext,
  type BrandKit,
  type ProposalBlock,
  type ProposalDocModel,
} from './proposalBlocks';
import type { BusinessDoc } from '../db/types';

/** Default brand kit — the Argo identity from lib/tokens. */
export const ARGO_BRAND_KIT: BrandKit = {
  logoUrl: '',
  primary: C.ink950,
  accent: C.magenta,
  fontFamily: "'Manrope', 'Inter', system-ui, sans-serif",
};

/** USD formatting for merge tokens — must stay stable (it moves the hash). */
function usd(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/**
 * Derive the typed block model from the persisted BusinessDoc — hero
 * from the title, text from the body, one interactive pricing block.
 * Merge tokens resolve ONLY from fields the record actually carries;
 * anything unresolved stays visible and flagged (never blanked).
 */
export function buildModelForDoc(doc: BusinessDoc): ProposalDocModel {
  const blocks: ProposalBlock[] = [
    {
      kind: 'hero',
      id: `${doc.id}-hero`,
      headline: doc.title,
      subheadline: doc.ownerName
        ? 'Prepared for {{account.name}} by {{owner.name}}'
        : 'Prepared for {{account.name}}',
    },
  ];
  if (doc.body.trim() !== '') {
    blocks.push({ kind: 'text', id: `${doc.id}-body`, text: doc.body.trim() });
  }
  blocks.push({ kind: 'quote', id: `${doc.id}-pricing`, heading: 'Investment' });

  const ctx: Record<string, string | number> = {
    'account.name': doc.entityName,
  };
  if (doc.ownerName) ctx['owner.name'] = doc.ownerName;
  if (doc.value != null) ctx['deal.value'] = usd(doc.value);

  return mergeContext(createProposalDoc({ blocks, brandKit: ARGO_BRAND_KIT }), ctx);
}
